import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, FileUp, Landmark, ScrollText, Building2 } from "lucide-react";
import { api, errMsg, fmtDate } from "../lib/api";
import { DISTRICTS } from "../lib/categories";
import { Field, btnGhost, btnPrimary, inputCls } from "./ui";
import { RichText } from "../pages/CmsPage";

const DOC_STATUS = { pending: ["Em verificação", "bg-amber-100 text-amber-800"], verified: ["Verificado", "bg-emerald-100 text-emerald-800"], rejected: ["Rejeitado", "bg-red-100 text-red-700"] };

const Step = ({ ok, icon: Icon, title, children, testid }) => (
  <section className="bo-card flex flex-col gap-3 p-5" data-testid={testid}>
    <h3 className="flex items-center gap-2 font-bold">{ok ? <CheckCircle2 className="h-5 w-5 text-bo-green" /> : <Circle className="h-5 w-5 text-gray-300" />}<Icon className="h-4 w-4" />{title}</h3>
    {children}
  </section>
);

function Details({ ob, onSaved }) {
  const [f, setF] = useState({ ...ob.details, district: ob.details.district || DISTRICTS[0] });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => { e.preventDefault(); try { onSaved((await api.put("/partner/onboarding/details", f)).data); toast.success("Dados legais guardados"); } catch (e2) { toast.error(errMsg(e2)); } };
  return (
    <form onSubmit={save} className="grid gap-3 sm:grid-cols-3">
      <Field label="Nome legal / firma"><input required minLength={2} value={f.legal_name} onChange={set("legal_name")} className={inputCls} data-testid="ob-legal-name" /></Field>
      <Field label="NIF (9 dígitos)"><input required pattern="\d{9}" value={f.nif} onChange={set("nif")} className={inputCls} data-testid="ob-nif" /></Field>
      <Field label="Distrito"><select value={f.district} onChange={set("district")} className={inputCls} data-testid="ob-district">{DISTRICTS.map((d) => <option key={d}>{d}</option>)}</select></Field>
      <Field label="Morada da sede"><input required minLength={3} value={f.address} onChange={set("address")} className={inputCls} data-testid="ob-address" /></Field>
      <Field label="Responsável legal"><input required minLength={2} value={f.responsible_name} onChange={set("responsible_name")} className={inputCls} data-testid="ob-responsible" /></Field>
      <Field label="N.º documento do responsável"><input required minLength={3} value={f.responsible_id} onChange={set("responsible_id")} className={inputCls} data-testid="ob-responsible-id" /></Field>
      <button className={`${btnPrimary} w-fit`} data-testid="ob-details-save">Guardar dados</button>
    </form>
  );
}

function Bank({ ob, onSaved }) {
  const [f, setF] = useState({ holder: ob.bank.holder, bank_name: ob.bank.bank_name, iban: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => { e.preventDefault(); try { onSaved((await api.put("/partner/onboarding/bank", f)).data); setF({ ...f, iban: "" }); toast.success("Dados bancários guardados de forma cifrada"); } catch (e2) { toast.error(errMsg(e2)); } };
  return (
    <form onSubmit={save} className="grid gap-3 sm:grid-cols-3">
      {ob.bank.iban_masked && <p className="text-sm sm:col-span-3" data-testid="ob-iban-masked">IBAN registado: <span className="font-mono">{ob.bank.iban_masked}</span></p>}
      <Field label="Titular da conta"><input required minLength={2} value={f.holder} onChange={set("holder")} className={inputCls} data-testid="ob-bank-holder" /></Field>
      <Field label="Banco"><input required minLength={2} value={f.bank_name} onChange={set("bank_name")} className={inputCls} data-testid="ob-bank-name" /></Field>
      <Field label={ob.bank.iban_masked ? "Novo IBAN (substitui o atual)" : "IBAN"}><input required value={f.iban} onChange={set("iban")} placeholder="ST.." autoComplete="off" className={inputCls} data-testid="ob-bank-iban" /></Field>
      <button className={`${btnPrimary} w-fit`} data-testid="ob-bank-save">Guardar IBAN</button>
    </form>
  );
}

function Documents({ ob, onSaved }) {
  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
    try { onSaved((await api.post("/partner/onboarding/documents", fd)).data); toast.success("Documento enviado"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-col divide-y divide-gray-50">
      {Object.entries(ob.doc_kinds).map(([kind, label]) => {
        const doc = ob.documents.find((d) => d.kind === kind);
        const [st, cls] = doc ? DOC_STATUS[doc.status] : ["Em falta", "bg-gray-100 text-gray-600"];
        return (
          <div key={kind} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`ob-doc-${kind}`}>
            <span className="flex-1">{label}{doc?.note && <span className="block text-xs text-red-700">{doc.note}</span>}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs ${cls}`}>{st}</span>
            {doc?.status !== "verified" && <label className={`${btnGhost} cursor-pointer px-3 py-1.5`}><FileUp className="h-4 w-4" />{doc ? "Substituir" : "Enviar"}
              <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => upload(kind, e.target.files[0])} data-testid={`ob-doc-input-${kind}`} /></label>}
          </div>);
      })}
      <p className="pt-2 text-xs text-gray-500">PDF, JPG ou PNG até 10 MB. Os documentos ficam em armazenamento privado e só a equipa autorizada os pode consultar.</p>
    </div>
  );
}

function Contract({ ob, onSaved }) {
  const [c, setC] = useState(null);
  const [ok, setOk] = useState(false);
  useEffect(() => { api.get("/legal/partner-contract").then(({ data }) => setC(data)).catch(() => {}); }, []);
  const accept = async () => { try { onSaved((await api.post("/partner/onboarding/accept-contract", { version: c.version })).data); toast.success("Contrato aceite"); } catch (e) { toast.error(errMsg(e)); } };
  if (ob.checks.contract) return <p className="text-sm" data-testid="ob-contract-accepted">Aceite em {fmtDate(ob.contract.accepted_at)} (versão {ob.contract.version}).</p>;
  if (!c) return null;
  return (
    <div className="flex flex-col gap-3">
      <div className="max-h-56 overflow-y-auto rounded-xl border border-gray-100 p-3 text-sm"><RichText text={c.body} /></div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="ob-contract-checkbox" />Li e aceito o Contrato de Parceiro Comercial (versão {c.version})</label>
      <button disabled={!ok} onClick={accept} className={`${btnPrimary} w-fit`} data-testid="ob-contract-accept">Aceitar contrato</button>
    </div>
  );
}

export function StoreOnboarding() {
  const [ob, setOb] = useState(null);
  useEffect(() => { api.get("/partner/onboarding").then(({ data }) => setOb(data)).catch(() => setOb(false)); }, []);
  if (!ob) return null;
  return (
    <div className="flex flex-col gap-4" data-testid="store-onboarding">
      <p className={`rounded-xl px-4 py-3 text-sm font-semibold ${ob.complete ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="ob-status">
        {ob.complete ? "Onboarding completo e verificado." : ob.submitted ? "Onboarding enviado: a aguardar verificação dos documentos pela administração." : "Onboarding por completar: preencha todos os passos para a loja poder ser aprovada."}</p>
      <Step ok={ob.checks.details} icon={Building2} title="1. Identificação legal" testid="ob-step-details"><Details ob={ob} onSaved={setOb} /></Step>
      <Step ok={ob.checks.bank} icon={Landmark} title="2. Dados bancários" testid="ob-step-bank"><Bank ob={ob} onSaved={setOb} /></Step>
      <Step ok={ob.checks.documents} icon={FileUp} title="3. Documentos" testid="ob-step-docs"><Documents ob={ob} onSaved={setOb} /></Step>
      <Step ok={ob.checks.contract} icon={ScrollText} title="4. Contrato de parceiro" testid="ob-step-contract"><Contract ob={ob} onSaved={setOb} /></Step>
    </div>
  );
}
